import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import HouseModal from '../../components/admin/HouseModal';
import Modal from '../../components/common/Modal';
import { useAppState } from '../../context/AppStateContext';

const HouseList = () => {
    const { get, del } = useApi();
    const { showToast } = useAppState();
    const [houses, setHouses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingHouse, setEditingHouse] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [houseToDelete, setHouseToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const data = await get('/api/houses/');
            setHouses(data);
        } catch (error) {
            console.error('Error fetching house data:', error);
        } finally {
            setLoading(false);
        }
    }, [get]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDeleteClick = (house) => {
        setHouseToDelete(house);
    };

    const confirmDelete = async () => {
        if (!houseToDelete) return;
        setDeleting(true);
        try {
            await del(`/api/houses/${houseToDelete.id}/`);
            showToast('House successfully removed', 'success');
            setHouseToDelete(null);
            fetchData();
        } catch (error) {
            console.error('Error deleting house:', error);
            showToast('Failed to remove house.', 'error');
        } finally {
            setDeleting(false);
        }
    };

    const openEditModal = (house) => {
        setEditingHouse(house);
        setShowModal(true);
    };

    const filteredHouses = houses.filter(house =>
        house.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (house.motto && house.motto.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (house.house_teacher_name && house.house_teacher_name.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    if (loading) return <div className="py-20 text-center animate-pulse text-[10px] font-black text-slate-300 uppercase tracking-widest">Querying House Registry...</div>;

    return (
        <div className="space-y-10">
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-black text-[#18216D]">House Registry</h1>
                    <p className="text-slate-400 font-bold text-sm mt-1">Institutional Identity & Governance</p>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                    <div className="relative group w-full md:w-64">
                        <i className="fas fa-search absolute left-5 top-1/2 -translate-y-1/2 text-slate-300"></i>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="SEARCH HOUSES..."
                            className="w-full pl-12 pr-6 py-4 bg-white border-2 border-slate-200 rounded-2xl focus:border-[#FFC425] transition-all text-[10px] font-black uppercase tracking-widest text-[#18216D] placeholder-slate-300 outline-none"
                        />
                    </div>

                    <button
                        onClick={() => { setEditingHouse(null); setShowModal(true); }}
                        className="px-10 py-4 bg-[#18216D] text-white rounded-[1.2rem] shadow-[0_20px_40px_-10px_rgba(24,33,109,0.3)] font-black text-[10px] uppercase tracking-[0.2em] hover:bg-[#1e2985] transition-all flex items-center gap-3"
                    >
                        <i className="fas fa-plus-circle text-xs"></i>
                        New House
                    </button>
                </div>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredHouses.map((house, idx) => (
                    <div
                        key={house.id}
                        className="bg-white rounded-[2rem] p-6 border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-indigo-900/5 transition-all group relative overflow-hidden"
                    >
                        <div 
                            className="absolute top-0 right-0 w-32 h-32 opacity-[0.03] group-hover:opacity-[0.08] transition-opacity rounded-full -mr-16 -mt-16"
                            style={{ backgroundColor: house.color || '#18216D' }}
                        ></div>

                        <div className="flex items-center gap-4 mb-6">
                            <div 
                                className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg"
                                style={{ backgroundColor: house.color || '#18216D' }}
                            >
                                <i className="fas fa-shield-halved text-lg"></i>
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-[#18216D] uppercase tracking-tight">{house.name}</h3>
                                {house.motto && <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic mt-0.5">{house.motto}</p>}
                            </div>
                        </div>

                        <div className="space-y-4 mb-8">
                            <div className="flex items-center justify-between py-3 border-b border-slate-50">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">House Master</span>
                                <span className="text-xs font-black text-[#18216D]">{house.house_teacher_name || 'Not Assigned'}</span>
                            </div>
                            <div className="flex items-center justify-between py-3 border-b border-slate-50">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Students</span>
                                <span className="px-3 py-1 bg-slate-50 text-[#18216D] rounded-lg text-[10px] font-black border border-slate-100">{house.student_count} Scholars</span>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={() => openEditModal(house)}
                                className="flex-1 py-3 bg-slate-50 text-[#18216D] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-white border border-transparent hover:border-slate-100 transition-all"
                            >
                                Edit
                            </button>
                            <button
                                onClick={() => handleDeleteClick(house)}
                                className="px-4 py-3 bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-500 hover:text-white transition-all"
                            >
                                <i className="fas fa-trash-alt"></i>
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {filteredHouses.length === 0 && (
                <div className="py-20 text-center">
                    <p className="text-slate-300 font-black uppercase tracking-[0.3em] text-[10px] italic">No identity protocols matching your query</p>
                </div>
            )}

            {showModal && (
                <HouseModal
                    house={editingHouse}
                    onClose={() => setShowModal(false)}
                    onSuccess={fetchData}
                />
            )}

            <Modal
                isOpen={!!houseToDelete}
                onClose={() => setHouseToDelete(null)}
                title="Remove House Protocol"
                type="danger"
                footer={
                    <>
                        <button
                            onClick={confirmDelete}
                            disabled={deleting}
                            className="flex-1 px-8 py-4 bg-rose-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-rose-600 shadow-xl transition-all"
                        >
                            {deleting ? 'Removing...' : 'Confirm Removal'}
                        </button>
                        <button
                            onClick={() => setHouseToDelete(null)}
                            className="bg-slate-50 text-slate-400 px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em]"
                        >
                            Cancel
                        </button>
                    </>
                }
            >
                <p>Are you certain you wish to dissolve the <span className="text-[#18216D] font-black uppercase">{houseToDelete?.name}</span> house? This will leave its scholars unassigned.</p>
            </Modal>
        </div>
    );
};

export default HouseList;
